import type { OutgoingStatisticsResponse } from '../../../store/reducers/OutgoingStatisticsSlice';

import type { OutgoingStatisticMetrics } from './outgoingStatisticMetrics';
import type { OutgoingStatisticRow } from './statisticRowTypes';
import { createMetricRow } from './statisticRowFactory';

const SECTION = 'Причины отсутствия целевого результата';

const calculateResidualCount = (
  totalCount: number,
  knownReasonCounts: readonly number[],
) =>
  Math.max(
    knownReasonCounts.reduce((residual, count) => residual - count, totalCount),
    0,
  );

const createTaskStatusChildren = (
  statistic: OutgoingStatisticsResponse,
  metrics: OutgoingStatisticMetrics,
): OutgoingStatisticRow[] => {
  const knownReasonCounts = [
    statistic.answeringMachineTasksCount,
    statistic.noAnswerTasksCount,
    statistic.errorTasksCount,
    statistic.hangUpTasksCount,
  ] as const;
  const notRecognizedCount = calculateResidualCount(
    statistic.failedTasksCount,
    knownReasonCounts,
  );

  return [
    createMetricRow({
      key: 'failedReasons-answeringMachineTasksCount',
      section: SECTION,
      parameter: 'Автоответчик',
      value: statistic.answeringMachineTasksCount,
      denominator: metrics.processedTasksCount,
      level: 1,
    }),
    createMetricRow({
      key: 'failedReasons-noAnswerTasksCount',
      section: SECTION,
      parameter: 'Нет ответа',
      value: statistic.noAnswerTasksCount,
      denominator: metrics.processedTasksCount,
      level: 1,
    }),
    createMetricRow({
      key: 'failedReasons-errorTasksCount',
      section: SECTION,
      parameter: 'Ошибка',
      value: statistic.errorTasksCount,
      denominator: metrics.processedTasksCount,
      level: 1,
    }),
    createMetricRow({
      key: 'failedReasons-hangUpTasksCount',
      section: SECTION,
      parameter: 'Сброс звонка абонентом',
      value: statistic.hangUpTasksCount,
      denominator: metrics.processedTasksCount,
      level: 1,
    }),
    createMetricRow({
      key: 'failedReasons-notRecognizedTasksCount',
      section: SECTION,
      parameter: 'Не распознана речь',
      value: notRecognizedCount,
      denominator: metrics.processedTasksCount,
      level: 1,
    }),
  ];
};

const createAttemptStatusChildren = (
  statistic: OutgoingStatisticsResponse,
  metrics: OutgoingStatisticMetrics,
): OutgoingStatisticRow[] => {
  const knownReasonCounts = [
    statistic.botDetectedAttemptCount,
    statistic.noAnswerAttemptCount,
    statistic.errorAttemptCount,
    statistic.hangUpAttemptCount,
  ] as const;
  const notRecognizedCount = calculateResidualCount(
    statistic.failedTasksAttemptCount,
    knownReasonCounts,
  );

  return [
    createMetricRow({
      key: 'failedReasons-botDetectedAttemptCount',
      section: SECTION,
      parameter: 'Автоответчик',
      value: statistic.botDetectedAttemptCount,
      denominator: metrics.processedTasksAttemptCount,
      level: 1,
    }),
    createMetricRow({
      key: 'failedReasons-noAnswerAttemptCount',
      section: SECTION,
      parameter: 'Нет ответа',
      value: statistic.noAnswerAttemptCount,
      denominator: metrics.processedTasksAttemptCount,
      level: 1,
    }),
    createMetricRow({
      key: 'failedReasons-errorAttemptCount',
      section: SECTION,
      parameter: 'Ошибка',
      value: statistic.errorAttemptCount,
      denominator: metrics.processedTasksAttemptCount,
      level: 1,
    }),
    createMetricRow({
      key: 'failedReasons-hangUpAttemptCount',
      section: SECTION,
      parameter: 'Сброс звонка абонентом',
      value: statistic.hangUpAttemptCount,
      denominator: metrics.processedTasksAttemptCount,
      level: 1,
    }),
    createMetricRow({
      key: 'failedReasons-notRecognizedAttemptCount',
      section: SECTION,
      parameter: 'Не распознана речь',
      value: notRecognizedCount,
      denominator: metrics.processedTasksAttemptCount,
      level: 1,
    }),
  ];
};

export const buildFailedReasonTaskRows = (
  statistic: OutgoingStatisticsResponse,
  metrics: OutgoingStatisticMetrics,
): OutgoingStatisticRow[] => [
  createMetricRow({
    key: 'failedReasons-notReceivedTasksCount',
    section: SECTION,
    parameter: 'Не удалось дозвониться',
    value: statistic.failedTasksCount,
    denominator: metrics.processedTasksCount,
    children: createTaskStatusChildren(statistic, metrics),
  }),
  createMetricRow({
    key: 'failedReasons-notIdentifiedTasksCount',
    section: SECTION,
    parameter: 'Не идентифицирован',
    value: statistic.notIdentifiedTasksCount,
    denominator: metrics.processedTasksCount,
  }),
  createMetricRow({
    key: 'failedReasons-misCancelledTasksCount',
    section: SECTION,
    parameter: 'Отменено МИС',
    value: metrics.noAttemptTasksCount,
    denominator: metrics.processedTasksCount,
  }),
];

export const buildFailedReasonCallRows = (
  statistic: OutgoingStatisticsResponse,
  metrics: OutgoingStatisticMetrics,
): OutgoingStatisticRow[] => [
  createMetricRow({
    key: 'failedReasons-notReceivedAttemptCount',
    section: SECTION,
    parameter: 'Не удалось дозвониться',
    value: statistic.failedTasksAttemptCount,
    denominator: metrics.processedTasksAttemptCount,
    children: createAttemptStatusChildren(statistic, metrics),
  }),
  createMetricRow({
    key: 'failedReasons-notIdentifiedAttemptCount',
    section: SECTION,
    parameter: 'Не идентифицирован',
    value: metrics.notIdentifiedAttemptCount,
    denominator: metrics.processedTasksAttemptCount,
  }),
  createMetricRow({
    key: 'failedReasons-misCancelledAttemptCount',
    section: SECTION,
    parameter: 'Отменено МИС',
    value: metrics.misCancelledAttemptCount,
    denominator: metrics.processedTasksAttemptCount,
  }),
];
